import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
} from 'react-native';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useNotifications, NotificationItem } from '@/hooks/common/useNotifications';
import { NOTIFICATIONS_MAX_MARK } from '@/constants/residents/notifications';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { strings } from '@/constants/residents/strings';
import { UnifiedModal } from '@/components/common/UnifiedModal';
import { NotificationAvatar } from './NotificationAvatar';
import { NotificationEmptyState } from './NotificationEmptyState';

interface MobileNotificationsModalProps {
  visible: boolean;
  onClose: () => void;
  onSeeAll: () => void;
  onSettings?: () => void;
  onPropertyChatPress?: () => void;
}

export const MobileNotificationsModal: React.FC<MobileNotificationsModalProps> = ({
  visible,
  onClose,
  onSeeAll,
  onSettings,
  onPropertyChatPress,
}) => {
  const { colors } = useThemeColors();
  const { notifications, isLoading, isUnread, isPropertyChat, formatDateMMMdd, markFirstNAsRead, markPropertyChatAsViewed } = useNotifications();

  React.useEffect(() => {
    if (visible && Array.isArray(notifications) && notifications.length > 0) {
      markFirstNAsRead(notifications, NOTIFICATIONS_MAX_MARK);
    }
  }, [visible, notifications, markFirstNAsRead]);

  const handleSettings = () => {
    if (onSettings) {
      onClose();
      onSettings();
    }
  };

  const ListSeparator = () => (
    <View style={[styles.separatorLine, { backgroundColor: colors.separator }]} />
  );

  const renderNotificationRow = ({ item }: { item: NotificationItem }) => {
    const unread = isUnread(item);
    const isPropertyChatItem = isPropertyChat(item);

    const isMarketplaceFeedback = item.notificationType === 'marketplace_feedback';
    const isMarketplaceNotification = item.notificationType === 'marketplace_notification';
    const isMarketplace = isMarketplaceFeedback || isMarketplaceNotification;

    const listingImageUrl = isMarketplaceFeedback
      ? item.feedbackRequest?.listing?.image
      : isMarketplaceNotification
        ? item.marketplaceNotification?.metadata?.listingImageUrl
        : undefined;

    const handlePress = () => {
      if (isPropertyChatItem) {
        markPropertyChatAsViewed();
      }
      onClose();
      if (isPropertyChatItem && onPropertyChatPress) {
        onPropertyChatPress();
      } else {
        onSeeAll();
      }
    };

    const avatarVariant = isPropertyChatItem ? 'message' : isMarketplace ? 'marketplace' : 'property';

    return (
      <TouchableOpacity
        style={styles.notificationRow}
        onPress={handlePress}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel={`${strings.notifications.mobile.defaultTitle}: ${item.title}`}
      >
        <NotificationAvatar
          showUnreadDot={unread}
          variant={avatarVariant}
          imageUrl={listingImageUrl}
        />

        <View style={styles.textContainer}>
          <View style={styles.rowHeader}>
            <Text style={[styles.title, { color: colors.text.primary }]} numberOfLines={1}>
              {item.title || strings.notifications.mobile.defaultTitle}
            </Text>
            <Text style={[styles.date, { color: colors.text.muted }]}>{formatDateMMMdd(item.createdAt)}</Text>
          </View>
          <Text style={[styles.description, { color: colors.text.secondary }]} numberOfLines={2}>
            {item.description || item.descriptionHtml || ''}
          </Text>
        </View>
      </TouchableOpacity>
    );
  };

  const keyExtractor = (item: NotificationItem) => `modal-notification-${item.id}`;

  const settingsButton = onSettings ? (
    <TouchableOpacity
      onPress={handleSettings}
      hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
      accessibilityRole="button"
      accessibilityLabel={strings.notifications.settings.title}
      style={[styles.headerButton, { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder }]}
    >
      <RESIDENTS_SVG_COMPONENTS.COG
        width={20}
        height={20}
        color={colors.text.muted}
      />
    </TouchableOpacity>
  ) : undefined;

  const footerContent = (
    <TouchableOpacity
      style={styles.seeAllButton}
      onPress={() => {
        onClose();
        onSeeAll();
      }}
      accessibilityRole="button"
      accessibilityLabel={strings.notifications.mobile.seeAll}
    >
      <Text style={styles.seeAllText}>{strings.notifications.mobile.seeAll}</Text>
      <RESIDENTS_SVG_COMPONENTS.CHEVRON_RIGHT
        width={20}
        height={20}
        color={PRIMITIVE_COLORS.resioBlueLight}
      />
    </TouchableOpacity>
  );

  return (
    <UnifiedModal
      visible={visible}
      onClose={onClose}
      variant="fullscreen"
      title={strings.notifications.mobile.modalTitle}
      headerRight={settingsButton}
      dismissOnBackdrop={false}
      footer={footerContent}
      footerStyle={styles.footerInner}
    >
      <View style={styles.body}>
        {isLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.text.muted} />
            <Text style={[styles.loadingText, { color: colors.text.muted }]}>{strings.notifications.mobile.loading}</Text>
          </View>
        ) : !notifications || notifications.length === 0 ? (
          <NotificationEmptyState />
        ) : (
          <FlatList
            data={notifications}
            keyExtractor={keyExtractor}
            renderItem={renderNotificationRow}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.listContent}
            ItemSeparatorComponent={ListSeparator}
          />
        )}
      </View>
    </UnifiedModal>
  );
};

const styles = StyleSheet.create({
  headerButton: {
    width: 40,
    height: 40,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },

  body: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  loadingText: {
    ...REDESIGN_TYPOGRAPHY.body,
  },
  listContent: {
    paddingVertical: 8,
  },
  notificationRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 24,
    paddingVertical: 16,
    gap: 16,
  },
  textContainer: {
    flex: 1,
    gap: 4,
  },
  rowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    ...REDESIGN_TYPOGRAPHY.cardTitle,
    lineHeight: 21,
    flex: 1,
    marginRight: 8,
  },
  date: {
    ...REDESIGN_TYPOGRAPHY.caption,
    lineHeight: 18,
  },
  description: {
    ...REDESIGN_TYPOGRAPHY.body,
    lineHeight: 21,
  },
  separatorLine: {
    height: 1,
    marginHorizontal: 24,
  },

  footerInner: {
    alignItems: 'center',
  },
  seeAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  seeAllText: {
    ...REDESIGN_TYPOGRAPHY.small,
    color: PRIMITIVE_COLORS.resioBlueLight,
    lineHeight: 21,
  },
});

export default MobileNotificationsModal;
